'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';

import { useRouter } from 'next/navigation';

import { Autocomplete, TextField, CircularProgress, Box } from '@mui/material';

import { API_URL } from '@/lib/consts';

import { DisplayConceptRef } from '../lib/api';

interface TopicSelectorProps {
  instanceId: string;
  onChangeFn?: (value: DisplayConceptRef | null) => void;
}

export const TopicSelector: React.FC<TopicSelectorProps> = ({ instanceId, onChangeFn }) => {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<DisplayConceptRef[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const autocompleteRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const searchConcepts = useCallback(async (query: string) => {
    if (!query) {
      setOptions([]);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/schema/prefix_search/${query}`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Search failed');
      const data: DisplayConceptRef[] = await response.json();
      setOptions(data);
    } catch (error) {
      console.error('Error searching concepts:', error);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchConcepts(inputValue);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [inputValue, searchConcepts]);

  // Global "/" keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === '/' && !isUserTyping()) {
        event.preventDefault();
        inputRef.current?.focus();
      }
    };

    const isUserTyping = () => {
      const activeElement = document.activeElement;
      return (
        activeElement instanceof HTMLInputElement || activeElement instanceof HTMLTextAreaElement
      );
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleChange = (_event: any, newValue: DisplayConceptRef | null) => {
    if (newValue) {
      if (onChangeFn) {
        onChangeFn(newValue);
      } else {
        router.push(`/guide/${encodeURIComponent(newValue.cref)}`);
      }
      setInputValue('');
      setOptions([]);
    }
  };

  return (
    <Box
      sx={{
        width: '90%',
        maxWidth: 500,
        margin: '0.8em',
      }}
    >
      <Autocomplete
        ref={autocompleteRef}
        id={`topic-selector-${instanceId}`}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        options={options}
        loading={loading}
        inputValue={inputValue}
        onInputChange={(_event, newInputValue) => setInputValue(newInputValue)}
        onChange={handleChange}
        getOptionLabel={(option) => option.display}
        isOptionEqualToValue={(option, value) => option.cref === value.cref}
        noOptionsText="No topics found"
        loadingText="Searching..."
        clearOnBlur={true}
        selectOnFocus={false}
        clearOnEscape={true}
        includeInputInList
        filterOptions={(x) => x} // Disable client-side filtering since we use server-side search
        renderInput={(params) => (
          <TextField
            {...params}
            inputRef={inputRef}
            placeholder="Search topics..."
            variant="outlined"
            size="small"
            fullWidth
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <Box component="li" {...props}>
            <Box>
              <Box component="span" fontWeight="bold">
                {option.display}:
              </Box>{' '}
              <Box component="span" color="text.secondary">
                {option.description}
              </Box>
            </Box>
          </Box>
        )}
        sx={{
          width: '100%',
          '& .MuiAutocomplete-listbox': {
            maxHeight: 300,
          },
        }}
      />
    </Box>
  );
};
